import { useEffect, useRef } from 'react';
import './App.css';
import { compileShader, createProgram, createBuffer, setupAttribute } from './utils/webgl';
import { vertexShaderSource, fragmentShaderSource } from './shaders/gradient';

function GradientBackground() {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const programRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas.getContext('webgl');

    if (!gl) {
      console.error('WebGL not supported');
      return;
    }

    // Compile shaders
    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    
    if (!vertexShader || !fragmentShader) return;

    // Create program
    const program = createProgram(gl, vertexShader, fragmentShader);
    if (!program) return;
    programRef.current = program;
    
    // Create vertex data for a full-screen quad
    const vertices = new Float32Array([
      -1, -1,
      1, -1,
      -1, 1,
      1, 1
    ]);
    
    // Create and bind buffer
    createBuffer(gl, vertices);
    
    // Set up attribute
    setupAttribute(gl, program, 'position', 2);
    
    // Get uniform locations
    const timeLocation = gl.getUniformLocation(program, 'uTime');
    const resolutionLocation = gl.getUniformLocation(program, 'uResolution');
    const color1Location = gl.getUniformLocation(program, 'uColor1');
    const color2Location = gl.getUniformLocation(program, 'uColor2');
    const color3Location = gl.getUniformLocation(program, 'uColor3');

    // Stripe-like colors (purple, blue, indigo)
    const color1 = [0.6, 0.4, 1.0]; // Purple
    const color2 = [0.2, 0.4, 1.0]; // Blue
    const color3 = [0.3, 0.2, 0.8]; // Indigo

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const render = () => {
      const time = (Date.now() - startTimeRef.current) / 1000;

      gl.useProgram(program);
      
      // Update uniforms
      gl.uniform1f(timeLocation, time);
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      gl.uniform3fv(color1Location, color1);
      gl.uniform3fv(color2Location, color2);
      gl.uniform3fv(color3Location, color3);
      
      // Draw
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      
      animationFrameRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (program) {
        gl.deleteProgram(program);
      }
      if (vertexShader) {
        gl.deleteShader(vertexShader);
      }
      if (fragmentShader) {
        gl.deleteShader(fragmentShader);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="gradient-background"
      style={{ position: 'fixed', top: 0, left: 0, zIndex: -1 }}
    />
  );
}

function ProductGallery() {
  const products = [
    {
      src: '/assets/cpj.jpeg',
      alt: 'Checkpoint Jam - Product Image',
      link: 'https://t.me/checkpointjambot',
      name: 'Checkpoint Jam'
    },
    {
      src: '/assets/mb.jpeg',
      alt: 'MessageBack - Product Image',
      link: 'https://t.me/messagebackbot',
      name: 'MessageBack'
    },
    {
      src: '/assets/rkk.jpeg',
      alt: 'Rain Korkor - Product Image',
      link: 'https://t.me/rainkorkorbot',
      name: 'Rain Korkor'
    },
    {
      src: '/assets/sn.jpeg',
      alt: 'The Slurpee Network - Product Image',
      link: 'https://t.me/slurpeebot',
      name: 'The Slurpee Network'
    },
    {
      src: '/assets/ss.jpeg',
      alt: 'SteadyShuttle - Product Image',
      link: 'https://t.me/steadyshuttlebot',
      name: 'SteadyShuttle'
    }
  ];

  return (
    <div className="product-gallery">
      {products.map((product, index) => (
        <a
          key={product.src}
          href={product.link}
          target="_blank"
          rel="noopener noreferrer"
          className={`product-link ${index === 0 ? 'first' : ''} ${
            index === products.length - 1 ? 'last' : ''
          }`}
        >
          <div className="product-image-container">
            <img
              src={product.src}
              alt={product.alt}
              className="product-image"
            />
          </div>
        </a>
      ))}
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <GradientBackground />
      <header className="App-header">
        <h1 className="brand-text">durianbit</h1>
        <p className="brand-subtitle">our products</p>
        <ProductGallery />
      </header>
    </div>
  );
}

export default App;
